import type { BackupKind } from '../../domain/backup';
import { EQ_BANDS, EQ_MAX_DB, EQ_MIN_DB, EQ_PRESET_ORDER } from '../../domain/eq';
import type { EqPreset } from '../../domain/settings';
import { type Lang, plural, translate } from '../../i18n';
import { clear, el } from '../../lib/dom';
import type { AppState } from '../../store/app';
import { createTabs } from '../components/tabs';
import { formatDecibels, formatHertz } from '../format';

/**
 * Zaslon Nastavitve (P2 §6).
 *
 * Štirje zavihki (**B-4**): **videz** (tema, jezik — faza 5), **zvok**
 * (ekvalizer in vizualizator — faza 6), **vaši podatki** (izvoz, uvoz,
 * prednaložene postaje — faza 2) in **vizitka** (o aplikaciji, politika
 * zasebnosti, tipkovnica — fazi 5 in 7).
 *
 * Izvoz je **prvorazreden, ne dodatek** (T2 §1, načelo 4):
 * `navigator.storage.persist()` ni jamstvo, Safari pa skriptno zapisljivo
 * shrambo izbriše po sedmih dneh brez interakcije.
 *
 * Vrstica je `.set-row` znotraj `.ao-set` (T5 §6). Tema in jezik sta izbira
 * enega iz dveh — `.ao-segctrl` z `aria-pressed`, ista komponenta kot
 * razvrščanje na Postajah.
 */

export interface SettingsScreenDeps {
  onExport(kind: BackupKind): void;
  /** Izbrana datoteka; branje in razčlenjevanje sta zunaj zaslona. */
  onImportFile(file: File): void;
  onClearSeed(): void;
  onRestoreSeed(): void;
  /** Koliko prednaloženih postaj manjka; 0 pomeni, da dejanje nima cilja. */
  missingSeedCount(): number;
  /** Počisti vse podatke — postaje, zgodovino in nastavitve (P2 §6.16). */
  onReset(): void;
  onSetTheme(theme: 'dark' | 'light'): void;
  onSetLang(lang: Lang): void;

  /* ── Zvok (P2 §6.3–6.5) ────────────────────────────────────────── */

  onEqEnabled(on: boolean): void;
  onEqPreset(preset: EqPreset): void;
  /** Ena ojačitev; celo polje sestavi klicno mesto iz trenutnih nastavitev. */
  onEqGain(index: number, db: number): void;
  onVisualizerEnabled(on: boolean): void;
  /**
   * Ali platforma Web Audio graf sploh zmore.
   *
   * Na iOS `createMediaElementSource()` za žive tokove ne dela (P2 §6.5).
   * Funkcija in ne vrednost samo zaradi testabilnosti — med sejo se ne
   * spremeni.
   */
  audioGraphSupported(): boolean;
  /** Uporabnik je poskusil vklopiti nekaj, česar platforma nima (P2 §6.5). */
  onUnsupported(): void;

  /** Za razdelek »o aplikaciji«. */
  appVersion: string;
  /**
   * Kratek commit sha gradnje — stoji ob različici.
   *
   * Različica pove, katera **izdaja** je; žig pove, katera **gradnja**.
   * Vprašanje na napravi je vedno drugo, ker se različica premakne nekajkrat
   * na leto, gradenj pa je več na dan.
   */
  buildSha: string;
}

interface Row {
  root: HTMLDivElement;
  title: HTMLDivElement;
  desc: HTMLDivElement;
  action: HTMLButtonElement;
}

function buildRow(actionClass: string): Row {
  const title = el('div', { class: 'set-title' });
  const desc = el('div', { class: 'set-desc' });
  const action = el('button', { class: actionClass, type: 'button' });

  const root = el('div', { class: 'set-row' }, [
    el('div', { class: 'set-main' }, [title, desc]),
    el('div', { class: 'set-ctrl' }, [action]),
  ]);

  return { root, title, desc, action };
}

interface SegRow<T extends string> {
  root: HTMLDivElement;
  title: HTMLDivElement;
  desc: HTMLDivElement;
  segments: { value: T; button: HTMLButtonElement }[];
}

/** Vrstica z izbiro enega iz dveh (tema, jezik). */
function buildSegRow<T extends string>(
  values: readonly T[],
  onPick: (value: T) => void,
): SegRow<T> {
  const title = el('div', { class: 'set-title' });
  const desc = el('div', { class: 'set-desc' });
  const segments = values.map((value) => {
    const button = el('button', { type: 'button', 'aria-pressed': 'false' });
    button.addEventListener('click', () => {
      onPick(value);
    });
    return { value, button };
  });
  const seg = el(
    'div',
    { class: 'ao-segctrl equal', role: 'group' },
    segments.map((s) => s.button),
  );
  const root = el('div', { class: 'set-row' }, [
    el('div', { class: 'set-main' }, [title, desc]),
    el('div', { class: 'set-ctrl' }, [seg]),
  ]);
  return { root, title, desc, segments };
}

interface ToggleRow {
  root: HTMLDivElement;
  title: HTMLDivElement;
  desc: HTMLDivElement;
  toggle: HTMLButtonElement;
}

/**
 * Vrstica s stikalom (`.ao-toggle`).
 *
 * ⚠️ **Stikalo ni `disabled`, tudi kadar platforma zmožnosti nima.** Onemogočen
 * gumb ne sproži klika, klik pa je edini trenutek, ko lahko povemo, **zakaj**
 * ne gre (P2 §6.5 zahteva glasno odpoved, ne tihe). Nedosegljivost nosi
 * `aria-disabled`, ki jo bralnik zaslona prebere, dotik pa vseeno pride do nas.
 */
function buildToggleRow(id: string, onPick: (next: boolean) => void): ToggleRow {
  const title = el('div', { class: 'set-title', id: `${id}Label` });
  const desc = el('div', { class: 'set-desc' });
  /**
   * ⚠️ **Ime stikala nosi `aria-labelledby`, ne vsebina.** `.ao-toggle` je
   * grafično stikalo brez besedila; brez povezave na naslov vrstice bi imelo
   * prazno dostopno ime in bralnik zaslona bi prebral samo »stikalo, izklopljeno«.
   */
  const toggle = el('button', {
    class: 'ao-toggle',
    type: 'button',
    id,
    role: 'switch',
    'aria-checked': 'false',
    'aria-labelledby': `${id}Label`,
  });
  toggle.addEventListener('click', () => {
    onPick(toggle.getAttribute('aria-checked') !== 'true');
  });
  const root = el('div', { class: 'set-row' }, [
    el('div', { class: 'set-main' }, [title, desc]),
    el('div', { class: 'set-ctrl' }, [toggle]),
  ]);
  return { root, title, desc, toggle };
}

/**
 * Datum zadnje spremembe politike zasebnosti (P2 §6.15).
 *
 * ⚠️ **Popravi ga vsakič, ko se besedilo politike spremeni.** Politika brez
 * datuma bralcu ne pove, ali gleda tisto, ki velja.
 *
 * Lokalni konstruktor in ne niz `'2026-07-22'`: niz ISO se razčleni kot polnoč
 * UTC in bi zahodno od Greenwicha izpisal prejšnji dan.
 */
const PRIVACY_UPDATED = new Date(2026, 6, 22);

/** Poti skozi naš strežnik — vrstni red je od najpogostejše navzdol. */
const PRIVACY_SERVER_PATHS = ['stream', 'probe', 'catalog', 'art'] as const;

/** Razdelki politike, ki so ena sama poved; `server` ima seznam in stoji zase. */
const PRIVACY_TOPICS = ['device', 'station', 'external', 'none'] as const;

interface PrivacyTopic {
  root: HTMLDetailsElement;
  summary: HTMLElement;
  body: HTMLDivElement;
}

/**
 * En razdelek politike zasebnosti — DS `.accordion` (`components.css`).
 *
 * **Nativni `<details>` in nič lastnega JS.** Tipkovnica ga odpre in zapre brez
 * naše kode, bralnik zaslona pozna stanje razprtosti, `summary` je v seznamu
 * elementov s focus ringom v `base.css`. Lastno stikalo bi vse tri stvari
 * moralo znova zgraditi.
 *
 * `.prose` na telesu je prav tako DS: brez njega bi bil `<ul>` v seznamu poti
 * brez oznak in zamika, ker `base.css` sezname resetira povsod razen tam.
 */
function buildPrivacyTopic(): PrivacyTopic {
  const summary = el('summary');
  const body = el('div', { class: 'acc-content prose' });
  const root = el('details', {}, [summary, body]);
  return { root, summary, body };
}

interface Band {
  root: HTMLDivElement;
  input: HTMLInputElement;
  db: HTMLSpanElement;
  hz: HTMLSpanElement;
  /** Uskladi `--eq-level` (−1..1) s trenutno vrednostjo drsnika. */
  syncLevel(): void;
}

/**
 * En pas ekvalizerja — kanonični markup DS (`app-objects.html` §11), od DS
 * v4.23.0 z **`.eq__track`** ([D2 §32](../../../docs/04-delivery/D2-ds-requests.md)).
 *
 * **Nič lastnega ARIA** (D2 §6): `<input type="range">` prinese cel APG vzorec
 * drsnika zastonj. Dodamo le `aria-valuetext` z enoto, ker sama številka brez
 * »decibelov« ni razumljiva, in `aria-orientation`, ker je drsnik navpičen.
 *
 * ⛔ **Range ostane nativen in prosojen.** `appearance: none` bi v Chromu ubil
 * navpično izrisovanje (`writing-mode`) in drsnik bi padel v vodoravnega —
 * izmerjeno ob v4.20.7. Zato vidno sled (žleb, 0 dB os, polnilo od sredine in
 * cilj 44px) riše `.eq__track`, range pa je samo interakcija.
 *
 * ⚠️ **Naša je natanko ena vrednost: `--eq-level`** (−1..1, predznačena). Vse
 * ostalo je DS. Kdor bo tu dodal `background`, `accent-color` ali `appearance`,
 * piše obvoz okoli komponente, ki tega ne potrebuje.
 */
function buildBand(index: number, onInput: (db: number) => void): Band {
  const input = el('input', {
    type: 'range',
    id: `eqBand${index}`,
    name: `eqBand${index}`,
    min: String(EQ_MIN_DB),
    max: String(EQ_MAX_DB),
    step: '1',
    value: '0',
    'aria-orientation': 'vertical',
  });

  const track = el('div', { class: 'eq__track' }, [
    el('span', { class: 'eq__fill up' }),
    el('span', { class: 'eq__fill dn' }),
    input,
  ]);
  const db = el('span', { class: 'db' });
  const hz = el('span', { class: 'hz' });
  const root = el('div', { class: 'eq__band' }, [track, db, hz]);

  /**
   * Polnilo je delež **največje** ojačitve, ne razpona: pri ±12 dB je `max`
   * 12, zato je −6 dB natanko pol poti od sredine navzdol. Deljenje z razponom
   * (24) bi dalo pol manjše polnilo in 0 dB os bi lagala.
   */
  function syncLevel(): void {
    root.style.setProperty('--eq-level', String(Number(input.value) / Number(input.max)));
  }

  input.addEventListener('input', () => {
    syncLevel();
    onInput(Number(input.value));
  });
  syncLevel();

  return { root, input, db, hz, syncLevel };
}

export function createSettingsScreen(
  root: HTMLElement,
  deps: SettingsScreenDeps,
): { update(state: AppState): void } {
  /* ── Videz ───────────────────────────────────────────────────────── */
  const themeRow = buildSegRow(['dark', 'light'] as const, deps.onSetTheme);
  const langRow = buildSegRow(['en', 'sl'] as const, deps.onSetLang);
  const appearance = el('div', { class: 'ao-set nw-settings' }, [themeRow.root, langRow.root]);

  /* ── Zvok (P2 §6.3–6.5) ──────────────────────────────────────────── */

  /**
   * ⛔ **Glasnosti tu NI — in to je odločitev, ne opustitev (B-5, 2026-07-22).**
   *
   * Vrstica je stala tu od faze 7, ker razširjenega pogleda za telefon še ni
   * bilo. Ko je prišel ([D2 §38](../../../docs/04-delivery/D2-ds-requests.md)),
   * je postala druga pot do istega kontrolnika — obe dolgi dva dotika. Na
   * napravi je bilo 2026-07-22 potrjeno, da je pot »razširi → glasnost«
   * očitna sama po sebi, zato je vrstica odpadla.
   *
   * ⚠️ **Razlaga za iOS je šla z njo v razširjen pogled**, ne v koš: tam
   * `HTMLMediaElement.volume` ne dela in ostane sam gumb za tišino. Brez
   * pojasnila bi bila to tiha odpoved, ki jo P2 §6.5 prepoveduje —
   * `player.volume.unsupported` v `player-full.ts`.
   */
  const eqRow = buildToggleRow('eqEnabled', (next) => {
    if (!deps.audioGraphSupported()) {
      deps.onUnsupported();
      return;
    }
    deps.onEqEnabled(next);
  });

  const presetSelect = el('select', { id: 'eqPreset', name: 'eqPreset' });
  presetSelect.addEventListener('change', () => {
    deps.onEqPreset(presetSelect.value as EqPreset);
  });
  const presetOptions = EQ_PRESET_ORDER.map((preset) => {
    const option = el('option', { value: preset });
    presetSelect.append(option);
    return { preset, option };
  });
  /**
   * »Po meri« je **izid, ne izbira**: nastane, ko uporabnik premakne drsnik.
   * V seznamu stoji samo takrat, ko dejansko velja — sicer bi bila edina
   * možnost, ki ob izbiri ne naredi ničesar.
   */
  const customOption = el('option', { value: 'custom' });

  const presetTitle = el('label', { class: 'set-title', for: 'eqPreset' });
  const presetRow = el('div', { class: 'set-row' }, [
    el('div', { class: 'set-main' }, [presetTitle]),
    el('div', { class: 'set-ctrl' }, [el('span', { class: 'select-wrap' }, [presetSelect])]),
  ]);

  const visualizerRow = buildToggleRow('visualizerEnabled', (next) => {
    if (!deps.audioGraphSupported()) {
      deps.onUnsupported();
      return;
    }
    deps.onVisualizerEnabled(next);
  });

  /**
   * ⚠️ **Zvok sta DVA okvira, ne eden — in vmes stoji banka drsnikov.**
   *
   * Banka mora stati **tik pod stikalom, ki jo prižge**. Dokler je bil okvir
   * en sam in je banka visela za njim, se je ob vklopu EQ odprla pod
   * **vizualizatorjem** — torej pod stikalom, s katerim nima zveze. Prijavljeno
   * z naprave 2026-07-22.
   *
   * Zakaj banka ni preprosto vrstica v okviru, pojasnjuje komentar pri
   * `eqPanel`: deset navpičnih drsnikov v `.set-ctrl` bi na 390px stisnilo
   * vsakega pod cilj dotika 44px. Ker torej mora biti zunaj okvira, mora biti
   * okvir prerezan tam, kjer banka pripada.
   */
  const sound = el('div', { class: 'ao-set nw-settings' }, [eqRow.root, presetRow]);
  const soundVisualizer = el('div', { class: 'ao-set nw-settings' }, [visualizerRow.root]);

  /**
   * Banka drsnikov stoji **pod** okvirom nastavitev in ne v njem: deset
   * navpičnih drsnikov v `.set-ctrl` bi na 390px stisnilo vsakega pod cilj
   * dotika 44px (D2 §6, zadnja alineja).
   */
  const bands = EQ_BANDS.map((_, index) =>
    buildBand(index, (db) => {
      deps.onEqGain(index, db);
    }),
  );
  const bank = el(
    'div',
    { class: 'eq nw-eq__bank', role: 'group' },
    bands.map((band) => band.root),
  );
  const eqReset = el('button', { class: 'btn secondary sm', type: 'button' });
  eqReset.addEventListener('click', () => {
    deps.onEqPreset('flat');
  });
  const eqPanel = el('div', { class: 'nw-eq' }, [bank, eqReset]);

  /* ── Vaši podatki ────────────────────────────────────────────────── */
  const exportStations = buildRow('btn secondary');
  const exportFull = buildRow('btn secondary');
  const importRow = buildRow('btn secondary');
  const seedRow = buildRow('btn secondary');
  const restoreRow = buildRow('btn secondary');
  // Rušilno dejanje: rdeč gumb (P4 — rušilno poimenuj jasno in obarvaj rdeče).
  const resetRow = buildRow('btn danger');

  exportStations.action.addEventListener('click', () => {
    deps.onExport('stations');
  });
  exportFull.action.addEventListener('click', () => {
    deps.onExport('full');
  });

  /**
   * Skrit `<input type="file">`, ki ga sproži gumb. Nativni izbirnik datotek
   * ni stiliran v nobenem DS in ne sme biti — sistemska pogovorna okna so
   * uporabnikova, ne naša.
   */
  const file = el('input', {
    type: 'file',
    id: 'importBackupFile',
    name: 'importBackupFile',
    accept: 'application/json,.json',
    hidden: '',
  });
  file.addEventListener('change', () => {
    const chosen = file.files?.[0];
    if (chosen === undefined) return;
    deps.onImportFile(chosen);
    file.value = '';
  });
  importRow.action.addEventListener('click', () => {
    file.click();
  });

  seedRow.action.addEventListener('click', deps.onClearSeed);
  restoreRow.action.addEventListener('click', deps.onRestoreSeed);
  resetRow.action.addEventListener('click', deps.onReset);

  const dataList = el('div', { class: 'ao-set nw-settings' }, [
    exportStations.root,
    exportFull.root,
    importRow.root,
    restoreRow.root,
    seedRow.root,
    resetRow.root,
  ]);

  /* ── Tipkovnica (P2 §8.8) ────────────────────────────────────────── */

  /**
   * Seznam bližnjic ni okras — je **edino mesto, kjer bližnjica obstaja**.
   *
   * Nevidna bližnjica je funkcija samo za tistega, ki jo že pozna. Opis nad
   * seznamom nosi pravilo, ki je pomembnejše od seznama: med tipkanjem v polje
   * bližnjice ne delujejo. Brez tega bi presledek sredi iskanja ustavil zvok in
   * uporabnik bi to razumel kot napako, ne kot bližnjico.
   */
  const KEY_ROWS = [
    { key: 'space', labelKey: 'settings.keys.playPause' },
    { key: '←', labelKey: 'settings.keys.previous' },
    { key: '→', labelKey: 'settings.keys.next' },
    { key: '↑', labelKey: 'settings.keys.volumeUp' },
    { key: '↓', labelKey: 'settings.keys.volumeDown' },
    { key: 'M', labelKey: 'settings.keys.mute' },
  ] as const;

  const keysHeading = el('h2', { class: 'nw-section__title' });
  const keysDesc = el('p', { class: 'nw-note' });
  const keyRows = KEY_ROWS.map((row) => {
    const title = el('div', { class: 'set-title' });
    const kbd = el('kbd', { class: 'kbd' });
    const root = el('div', { class: 'set-row' }, [
      el('div', { class: 'set-main' }, [title]),
      el('div', { class: 'set-ctrl fixed' }, [kbd]),
    ]);
    return { ...row, root, title, kbd };
  });
  const keys = el(
    'div',
    { class: 'ao-set nw-settings' },
    keyRows.map((row) => row.root),
  );

  /* ── O aplikaciji ────────────────────────────────────────────────── */
  const aboutHeading = el('h2', { class: 'nw-section__title' });
  const aboutName = el('div', { class: 'nw-about__name' });
  const aboutVersion = el('div', { class: 'nw-about__version tnum' });
  const aboutDesc = el('p', { class: 'nw-about__desc' });
  const aboutPromise = el('p', { class: 'nw-about__promise' });
  const aboutLicences = el('p', { class: 'nw-about__licences' });
  const about = el('div', { class: 'nw-about' }, [
    aboutName,
    aboutVersion,
    aboutDesc,
    aboutPromise,
    aboutLicences,
  ]);

  /* ── Zasebnost (P2 §6.15) ────────────────────────────────────────── */

  /**
   * Politika zasebnosti je **obveznost pred javno objavo**, ne funkcija
   * ([D3 #7](../../../docs/04-delivery/D3-open-questions.md)).
   *
   * Stoji razprta v razdelke in ne kot en blok besedila iz dveh razlogov:
   * bralec skoraj vedno išče eno stvar (»kaj gre ven?«), in razprt razdelek je
   * krajša pot do nje kot drsenje po zidu. Drugi razlog je vzdrževanje —
   * razdelek, ki opisuje eno pot v kodi, se ob spremembi te poti popravi sam
   * zase.
   */
  const privacyHeading = el('h2', { class: 'nw-section__title' });
  const privacyIntro = el('p', { class: 'nw-note' });

  const privacyServer = buildPrivacyTopic();
  const privacyServerIntro = el('p');
  const privacyServerPaths = PRIVACY_SERVER_PATHS.map((path) => ({ path, item: el('li') }));
  const privacyServerLimit = el('p');
  privacyServer.body.append(
    privacyServerIntro,
    el(
      'ul',
      {},
      privacyServerPaths.map((entry) => entry.item),
    ),
    privacyServerLimit,
  );

  const privacyTopics = PRIVACY_TOPICS.map((topic) => {
    const built = buildPrivacyTopic();
    const text = el('p');
    built.body.append(text);
    return { topic, ...built, text };
  });

  /**
   * Vrstni red pripovedi: kaj ostane pri vas → kaj gre skozi nas → kaj vidi
   * postaja → kam vodi odhod iz aplikacije → česa ni.
   *
   * »Kaj ostane« gre prvo, ker je odgovor, ki ga večina išče; »kaj gre skozi
   * nas« takoj za njim, ker je edini, ki prvega omejuje. Zaporedje nosi ta
   * seznam in ne `PRIVACY_TOPICS` — `server` ima seznam poti namesto ene
   * povedi in ga zato ne gradi ista zanka.
   */
  const privacyAccordion = el('div', { class: 'accordion' }, [
    ...privacyTopics.slice(0, 1).map((entry) => entry.root),
    privacyServer.root,
    ...privacyTopics.slice(1).map((entry) => entry.root),
  ]);

  const privacyUpdated = el('p', { class: 'nw-note' });
  const privacy = el('div', { class: 'nw-privacy' }, [
    privacyIntro,
    privacyAccordion,
    privacyUpdated,
  ]);

  /* ── Zavihki (B-4) ───────────────────────────────────────────────── */

  /**
   * Nastavitve so bile en dolg drsni stolpec: videz, zvok z banko desetih
   * drsnikov, podatki, tipkovnica, o aplikaciji — in zdaj še politika
   * zasebnosti. Do zadnjega razdelka je bila pot **daljša od zaslona**, ne da
   * bi kateri koli razdelek zrasel po svoji krivdi.
   *
   * Vsebina se ni prepisala; obstoječi bloki so samo zaviti v štiri panele.
   * Napisi zavihkov so **isti ključi kot prejšnji naslovi razdelkov**
   * (`settings.*.heading`), zato ni ne novih nizov ne dveh imen za isto stvar.
   * Naslovi so iz treh panelov odpadli, ker jih zdaj nosi zavihek nad njimi in
   * bi drugače stali dvakrat; **»vizitka« jih obdrži**, ker združuje tri
   * razdelke in nobeden od njih ni zavihek sam.
   */
  const tabs = createTabs('settings', [
    { id: 'appearance', content: [appearance] },
    { id: 'sound', content: [sound, eqPanel, soundVisualizer] },
    { id: 'data', content: [dataList, file] },
    {
      id: 'info',
      /**
       * Vrstni red: **o aplikaciji → zasebnost → tipkovnica** (zahteva
       * lastnika 2026-07-22).
       *
       * »O aplikaciji« je prvo, ker je zavihek po njem poimenovan in ker je
       * različica prvo, kar kdo v vizitki išče. Zasebnost stoji takoj za njim,
       * ker je z njim v isti misli — kdo smo in kaj z vašim delamo. Tipkovnica
       * je zadnja: zanima najmanj ljudi in je najdaljša, zato bi vse ostalo
       * potisnila pod rob zaslona.
       */
      content: [aboutHeading, about, privacyHeading, privacy, keysHeading, keysDesc, keys],
    },
  ]);

  clear(root);
  root.append(tabs.list, ...tabs.panels);

  function updateSeg<T extends string>(row: SegRow<T>, active: T): void {
    for (const { value, button } of row.segments) {
      const on = value === active;
      button.classList.toggle('on', on);
      button.setAttribute('aria-pressed', String(on));
    }
  }

  function updateToggle(row: ToggleRow, on: boolean, supported: boolean): void {
    row.toggle.classList.toggle('on', on);
    row.toggle.setAttribute('aria-checked', String(on));
    // `aria-disabled` in ne `disabled`: klik mora priti do nas, da lahko
    // povemo, zakaj ne gre (P2 §6.5).
    row.toggle.setAttribute('aria-disabled', String(!supported));
  }

  return {
    update(state) {
      const { lang } = state;

      // Napisi zavihkov so isti ključi kot nekdanji naslovi razdelkov (B-4):
      // eno ime za eno stvar, in nobenega novega niza za prestavljeno vsebino.
      tabs.setListLabel(translate(lang, 'settings.tabs.label'));
      tabs.setLabel(0, translate(lang, 'settings.appearance.heading'));
      tabs.setLabel(1, translate(lang, 'settings.sound.heading'));
      tabs.setLabel(2, translate(lang, 'settings.data.heading'));
      tabs.setLabel(3, translate(lang, 'settings.info.heading'));

      themeRow.title.textContent = translate(lang, 'settings.theme.title');
      themeRow.desc.textContent = translate(lang, 'settings.theme.desc');
      themeRow.segments[0]?.button.replaceChildren(translate(lang, 'settings.theme.dark'));
      themeRow.segments[1]?.button.replaceChildren(translate(lang, 'settings.theme.light'));
      updateSeg(themeRow, state.theme);

      langRow.title.textContent = translate(lang, 'settings.lang.title');
      langRow.desc.textContent = translate(lang, 'settings.lang.desc');
      langRow.segments[0]?.button.replaceChildren(translate(lang, 'settings.lang.en'));
      langRow.segments[1]?.button.replaceChildren(translate(lang, 'settings.lang.sl'));
      updateSeg(langRow, lang);

      /* ── Zvok ─────────────────────────────────────────────────── */

      const { eqEnabled, eqPreset, eqGains, visualizerEnabled } = state.settings;
      const supported = deps.audioGraphSupported();

      eqRow.title.textContent = translate(lang, 'settings.eq.title');
      /**
       * Opis nosi **opozorilo o proxyju** (P2 §6.4, T1 §4): EQ prestavi tok na
       * našega strežnika in na počasnem mobilnem omrežju se to lahko zatika.
       * Kadar platforma EQ nima, je ta pojasnitev odveč in jo zamenja tista,
       * ki v tistem trenutku šteje.
       */
      eqRow.desc.textContent = translate(
        lang,
        supported ? 'settings.eq.desc' : 'settings.eq.unsupported',
      );
      updateToggle(eqRow, eqEnabled && supported, supported);

      presetTitle.textContent = translate(lang, 'settings.eq.preset');
      for (const { preset, option } of presetOptions) {
        option.textContent = translate(lang, `settings.eq.preset.${preset}` as const);
      }
      customOption.textContent = translate(lang, 'settings.eq.preset.custom');
      // »Po meri« se v seznam prikrade samo takrat, ko res velja.
      if (eqPreset === 'custom') presetSelect.append(customOption);
      else customOption.remove();
      presetSelect.value = eqPreset;

      visualizerRow.title.textContent = translate(lang, 'settings.visualizer.title');
      visualizerRow.desc.textContent = translate(
        lang,
        supported ? 'settings.visualizer.desc' : 'settings.eq.unsupported',
      );
      updateToggle(visualizerRow, visualizerEnabled && supported, supported);

      // Drsniki so vidni samo, kadar imajo kaj početi: banka desetih drsnikov
      // nad izklopljenim EQ je vabilo, ki ne vodi nikamor.
      const showBank = eqEnabled && supported;
      presetRow.toggleAttribute('hidden', !showBank);
      eqPanel.toggleAttribute('hidden', !showBank);

      bands.forEach((band, index) => {
        const hz = EQ_BANDS[index] ?? 0;
        const gain = eqGains[index] ?? 0;
        band.input.value = String(gain);
        // Vrednost lahko pride od zunaj (prednastavitev, uvoz) — polnilo mora
        // slediti tudi takrat, ne le ob premiku prsta.
        band.syncLevel();
        band.input.setAttribute('aria-label', formatHertz(lang, hz));
        // Sama številka brez enote ni razumljiva (D2 §6); slovenščina zahteva
        // vse štiri oblike — „1 decibel", „2 decibela", „3 decibele", „5 decibelov".
        band.input.setAttribute('aria-valuetext', formatDecibels(lang, gain));
        band.db.textContent = formatDecibels(lang, gain, { short: true });
        band.hz.textContent = formatHertz(lang, hz, { short: true });
      });
      bank.setAttribute('aria-label', translate(lang, 'settings.eq.title'));
      eqReset.textContent = translate(lang, 'settings.eq.reset');

      exportStations.title.textContent = translate(lang, 'settings.export.stations.title');
      exportStations.desc.textContent = translate(lang, 'settings.export.stations.desc');
      exportStations.action.textContent = translate(lang, 'settings.export.action');

      exportFull.title.textContent = translate(lang, 'settings.export.full.title');
      exportFull.desc.textContent = translate(lang, 'settings.export.full.desc');
      exportFull.action.textContent = translate(lang, 'settings.export.action');

      importRow.title.textContent = translate(lang, 'settings.import.title');
      importRow.desc.textContent = translate(lang, 'settings.import.desc');
      importRow.action.textContent = translate(lang, 'settings.import.action');

      // Dejanje je dodajanje, ne prepis, zato brez potrditve; koliko jih bo,
      // pa mora povedati VNAPREJ — vse štiri slovenske oblike (P4 §9).
      const missing = deps.missingSeedCount();
      restoreRow.title.textContent = translate(lang, 'settings.seed.restore.title');
      restoreRow.desc.textContent =
        missing === 0
          ? translate(lang, 'settings.seed.restore.desc')
          : plural(lang, 'settings.seed.restore.count', missing);
      restoreRow.action.disabled = missing === 0;
      restoreRow.action.textContent = translate(
        lang,
        missing === 0 ? 'settings.seed.restore.none' : 'settings.seed.restore.action',
      );

      seedRow.title.textContent = translate(lang, 'settings.seed.title');
      seedRow.desc.textContent = translate(lang, 'settings.seed.desc');
      const hasSeed = state.stations.some((station) => station.source === 'seed');
      seedRow.action.disabled = !hasSeed;
      seedRow.action.textContent = translate(
        lang,
        hasSeed ? 'settings.seed.action' : 'settings.seed.none',
      );

      // Vedno omogočen: tudi na praznem seznamu ponastavi nastavitve na privzeto.
      // Potrditev in dejansko brisanje sta v `main.ts` (rušilno = nikoli en klik).
      resetRow.title.textContent = translate(lang, 'settings.reset.title');
      resetRow.desc.textContent = translate(lang, 'settings.reset.desc');
      resetRow.action.textContent = translate(lang, 'settings.reset.action');

      keysHeading.textContent = translate(lang, 'settings.keys.heading');
      keysDesc.textContent = translate(lang, 'settings.keys.desc');
      for (const row of keyRows) {
        row.title.textContent = translate(lang, row.labelKey);
        // Puščice so isti znak v obeh jezikih; presledek ni — ta se prevaja.
        row.kbd.textContent =
          row.key === 'space' ? translate(lang, 'settings.keys.space') : row.key;
      }

      aboutHeading.textContent = translate(lang, 'settings.about.heading');
      aboutName.textContent = translate(lang, 'app.name');
      aboutVersion.textContent = translate(lang, 'settings.about.version', {
        version: deps.appVersion,
        build: deps.buildSha,
      });
      aboutDesc.textContent = translate(lang, 'settings.about.desc');
      aboutPromise.textContent = translate(lang, 'settings.about.promise');
      aboutLicences.textContent = translate(lang, 'settings.about.licences');

      /* ── Zasebnost ────────────────────────────────────────────── */

      privacyHeading.textContent = translate(lang, 'settings.privacy.heading');
      privacyIntro.textContent = translate(lang, 'settings.privacy.intro');

      privacyServer.summary.textContent = translate(lang, 'settings.privacy.server.title');
      privacyServerIntro.textContent = translate(lang, 'settings.privacy.server.intro');
      for (const { path, item } of privacyServerPaths) {
        item.textContent = translate(lang, `settings.privacy.server.${path}` as const);
      }
      privacyServerLimit.textContent = translate(lang, 'settings.privacy.server.limit');

      for (const entry of privacyTopics) {
        entry.summary.textContent = translate(lang, `settings.privacy.${entry.topic}.title`);
        entry.text.textContent = translate(lang, `settings.privacy.${entry.topic}.body`);
      }

      // Datum prek `Intl`, nikoli konkatenacija (P4 §7): slovenščina piše dan s
      // piko in mesec z malo začetnico, angleščina niti enega niti drugega.
      privacyUpdated.textContent = translate(lang, 'settings.privacy.updated', {
        date: new Intl.DateTimeFormat(lang, { dateStyle: 'long' }).format(PRIVACY_UPDATED),
      });
    },
  };
}
